import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Constrain view: an auto-framing camera. While active it keeps the whole
 * swarm in frame, looking straight on (along +Z, level), and dollies in/out
 * as the swarm's real extent changes — slide Size up and the frame smoothly
 * zooms out exactly as the drones reach the edge.
 *
 * The swarm's extent is measured LIVE each frame from any <points name="swarm">
 * in the scene: geometry bounding sphere × the material's uScale (DroneField
 * scales in the shader, so the geometry alone under-reports) + the cymatics
 * pump amplitude (also shader-side). Camera-operator easing: zooming OUT is
 * quick (never clip the swarm for long), zooming IN is lazy (calm reframing).
 */
export function ConstrainRig({ active }: { active: boolean }) {
  const cur = useRef<{ dist: number; c: THREE.Vector3 } | null>(null);
  const tmp = useRef(new THREE.Vector3());

  useFrame(({ camera, scene }, dtRaw) => {
    if (!active) {
      cur.current = null; // next activation re-syncs from wherever the camera is
      return;
    }
    const dt = Math.min(dtRaw, 1 / 30);

    // ---- measure the live swarm ----
    let radius = 0;
    const center = tmp.current.set(0, 0, 0);
    let found = false;
    scene.traverse((o) => {
      if (found || !(o as THREE.Points).isPoints || o.name !== "swarm") return;
      const pts = o as THREE.Points;
      const g = pts.geometry as THREE.BufferGeometry;
      g.computeBoundingSphere();
      const bs = g.boundingSphere;
      if (!bs || !isFinite(bs.radius)) return;
      const mat = pts.material as THREE.ShaderMaterial;
      const sc: number = mat.uniforms?.uScale ? mat.uniforms.uScale.value : 1;
      const cymAmp: number = mat.uniforms?.uCymAmp ? mat.uniforms.uCymAmp.value : 0;
      center.copy(bs.center).multiplyScalar(sc).applyMatrix4(pts.matrixWorld);
      radius = bs.radius * sc + cymAmp; // plate antinodes pump beyond the flat cloud
      found = true;
    });
    if (!found || radius <= 0) return;

    // ---- fit distance: the bounding sphere inside the tighter FOV axis ----
    const cam = camera as THREE.PerspectiveCamera;
    const vHalf = (cam.fov * Math.PI) / 360;
    const hHalf = Math.atan(Math.tan(vHalf) * cam.aspect);
    const half = Math.min(vHalf, hHalf);
    const MARGIN = 1.08;
    const targetDist = Math.max(2.5, (radius * MARGIN) / Math.sin(half));

    // ---- ease (take over smoothly from wherever the camera was) ----
    let t = cur.current;
    if (!t) {
      t = { dist: camera.position.distanceTo(center), c: center.clone() };
      cur.current = t;
    }
    const k = targetDist > t.dist ? 6 : 1.6; // fast out, lazy in
    t.dist += (targetDist - t.dist) * Math.min(1, dt * k);
    t.c.lerp(center, Math.min(1, dt * 2.5));

    camera.position.set(t.c.x, t.c.y, t.c.z + t.dist);
    camera.lookAt(t.c);
  });

  return null;
}
